import { useState } from 'react'
import { RecommendationsPanelProps } from '@/types/components'
import { Recommendation, RecommendationCategory } from '@/types'
import { 
  CheckCircle, 
  Clock, 
  AlertTriangle, 
  TrendingUp, 
  ChevronRight, 
  Filter,
  X,
  Play,
  Pause
} from 'lucide-react'
import { cn } from '@/utils/cn'

const CATEGORY_COLORS = {
  legal_compliance: {
    bg: 'bg-green-50 dark:bg-green-900/20',
    border: 'border-green-200 dark:border-green-800',
    text: 'text-green-700 dark:text-green-300',
    icon: 'text-green-600 dark:text-green-400'
  },
  financial_health: {
    bg: 'bg-blue-50 dark:bg-blue-900/20',
    border: 'border-blue-200 dark:border-blue-800',
    text: 'text-blue-700 dark:text-blue-300',
    icon: 'text-blue-600 dark:text-blue-400'
  },
  operational_efficiency: {
    bg: 'bg-yellow-50 dark:bg-yellow-900/20',
    border: 'border-yellow-200 dark:border-yellow-800',
    text: 'text-yellow-700 dark:text-yellow-300',
    icon: 'text-yellow-600 dark:text-yellow-400'
  },
  market_presence: {
    bg: 'bg-purple-50 dark:bg-purple-900/20',
    border: 'border-purple-200 dark:border-purple-800',
    text: 'text-purple-700 dark:text-purple-300',
    icon: 'text-purple-600 dark:text-purple-400'
  },
  data_quality: {
    bg: 'bg-gray-50 dark:bg-gray-900/20',
    border: 'border-gray-200 dark:border-gray-800',
    text: 'text-gray-700 dark:text-gray-300',
    icon: 'text-gray-600 dark:text-gray-400'
  }
}

const CATEGORY_LABELS = {
  legal_compliance: 'Legal Compliance',
  financial_health: 'Financial Health',
  operational_efficiency: 'Operational Efficiency',
  market_presence: 'Market Presence',
  data_quality: 'Data Quality'
}

const PRIORITY_COLORS = {
  low: 'text-gray-600 dark:text-gray-400',
  medium: 'text-yellow-600 dark:text-yellow-400',
  high: 'text-orange-600 dark:text-orange-400',
  critical: 'text-red-600 dark:text-red-400'
}

const EFFORT_LABELS = {
  low: 'Low Effort',
  medium: 'Medium Effort',
  high: 'High Effort'
}

export function RecommendationsPanel({
  recommendations,
  showCompleted = false,
  onComplete,
  onDismiss,
  className
}: RecommendationsPanelProps) {
  const [selectedCategory, setSelectedCategory] = useState<RecommendationCategory | 'all'>('all')
  const [selectedPriority, setSelectedPriority] = useState<string>('all')
  const [showFilters, setShowFilters] = useState(false)
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set())

  // Filter recommendations
  const filteredRecommendations = recommendations.filter(rec => {
    if (!showCompleted && rec.status === 'completed') return false
    if (selectedCategory !== 'all' && rec.category !== selectedCategory) return false
    if (selectedPriority !== 'all' && rec.priority !== selectedPriority) return false
    return true
  })

  // Group by priority
  const groupedRecommendations = filteredRecommendations.reduce((acc, rec) => {
    if (!acc[rec.priority]) acc[rec.priority] = []
    acc[rec.priority].push(rec)
    return acc
  }, {} as Record<string, Recommendation[]>)

  const toggleExpanded = (id: string) => {
    const newExpanded = new Set(expandedItems)
    if (newExpanded.has(id)) {
      newExpanded.delete(id)
    } else {
      newExpanded.add(id)
    }
    setExpandedItems(newExpanded)
  }

  const handleStatusChange = (recommendation: Recommendation, newStatus: Recommendation['status']) => {
    if (newStatus === 'completed' && onComplete) {
      onComplete(recommendation.id)
    }
    // In real app, this would update the recommendation status
  }

  const getStatusIcon = (status: Recommendation['status']) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-600" />
      case 'in_progress':
        return <Play className="w-4 h-4 text-blue-600" />
      case 'dismissed':
        return <X className="w-4 h-4 text-gray-600" />
      default:
        return <Clock className="w-4 h-4 text-gray-400" />
    }
  }

  return (
    <div className={cn(
      'bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm',
      className
    )}>
      {/* Header */}
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Recommendations
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {filteredRecommendations.length} actionable items to improve your score
            </p>
          </div>
          
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-3 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            <Filter className="w-4 h-4" />
            Filters
          </button>
        </div>

        {/* Filters */}
        {showFilters && (
          <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Category
                </label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value as RecommendationCategory | 'all')}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="all">All Categories</option>
                  {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Priority
                </label>
                <select
                  value={selectedPriority}
                  onChange={(e) => setSelectedPriority(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="all">All Priorities</option>
                  <option value="critical">Critical</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>

              <div className="flex items-end">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={showCompleted}
                    onChange={(e) => setShowCompleted?.(e.target.checked)}
                    className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    Show completed
                  </span>
                </label>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Recommendations List */}
      <div className="p-6">
        {filteredRecommendations.length === 0 ? (
          <div className="text-center py-8">
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              All caught up!
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              No recommendations match your current filters.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {Object.entries(groupedRecommendations)
              .sort(([a], [b]) => {
                const order = ['critical', 'high', 'medium', 'low']
                return order.indexOf(a) - order.indexOf(b)
              })
              .map(([priority, recs]) => (
                <div key={priority}>
                  <h3 className={cn(
                    'text-sm font-medium mb-3 capitalize',
                    PRIORITY_COLORS[priority as keyof typeof PRIORITY_COLORS]
                  )}>
                    {priority} Priority ({recs.length})
                  </h3>
                  
                  <div className="space-y-3">
                    {recs.map((recommendation) => {
                      const categoryColors = CATEGORY_COLORS[recommendation.category]
                      const isExpanded = expandedItems.has(recommendation.id)
                      
                      return (
                        <div
                          key={recommendation.id}
                          className={cn(
                            'border rounded-lg p-4 transition-all duration-200',
                            categoryColors.bg,
                            categoryColors.border
                          )}
                        >
                          {/* Header */}
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                {getStatusIcon(recommendation.status)}
                                <h4 className="font-medium text-gray-900 dark:text-white">
                                  {recommendation.title}
                                </h4>
                                <span className={cn(
                                  'px-2 py-1 text-xs font-medium rounded-full',
                                  categoryColors.bg,
                                  categoryColors.text
                                )}>
                                  {CATEGORY_LABELS[recommendation.category]}
                                </span>
                              </div>
                              
                              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                                {recommendation.description}
                              </p>
                              
                              <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                                <span className="flex items-center gap-1">
                                  <TrendingUp className="w-3 h-3" />
                                  +{recommendation.impact} points
                                </span>
                                <span>{EFFORT_LABELS[recommendation.effort]}</span>
                                <span>{recommendation.timeframe}</span>
                              </div>
                            </div>
                            
                            <button
                              onClick={() => toggleExpanded(recommendation.id)}
                              className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                            >
                              <ChevronRight className={cn(
                                'w-4 h-4 transition-transform',
                                isExpanded && 'rotate-90'
                              )} />
                            </button>
                          </div>

                          {/* Expanded Content */}
                          {isExpanded && (
                            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
                              <h5 className="font-medium text-gray-900 dark:text-white mb-3">
                                Action Steps
                              </h5>
                              
                              <div className="space-y-2">
                                {recommendation.actionSteps.map((step) => (
                                  <div key={step.id} className="flex items-start gap-3">
                                    <input
                                      type="checkbox"
                                      checked={step.completed}
                                      className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 mt-0.5"
                                      readOnly
                                    />
                                    <div className="flex-1">
                                      <p className={cn(
                                        'text-sm',
                                        step.completed 
                                          ? 'text-gray-500 dark:text-gray-400 line-through'
                                          : 'text-gray-700 dark:text-gray-300'
                                      )}>
                                        {step.title}
                                      </p>
                                      {step.description && (
                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                          {step.description}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>

                              {/* Action Buttons */}
                              <div className="flex items-center gap-2 mt-4">
                                {recommendation.status !== 'completed' && (
                                  <button
                                    onClick={() => handleStatusChange(recommendation, 'in_progress')}
                                    className="px-3 py-1 text-xs bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                                  >
                                    Start Working
                                  </button>
                                )}
                                
                                {recommendation.status !== 'completed' && (
                                  <button
                                    onClick={() => handleStatusChange(recommendation, 'completed')}
                                    className="px-3 py-1 text-xs bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                                  >
                                    Mark Complete
                                  </button>
                                )}
                                
                                {onDismiss && (
                                  <button
                                    onClick={() => onDismiss(recommendation.id)}
                                    className="px-3 py-1 text-xs text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                                  >
                                    Dismiss
                                  </button>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  )
}